/*
 * Peppermint WebMCP integration.
 *
 * This is deliberately dependency-free because the theme has no build step.
 * It progressively enhances the storefront when Chrome exposes
 * document.modelContext and keeps the normal Shopify forms as the fallback.
 */

(() => {
  "use strict";

  const MAX_RESULT_LENGTH = 1500;
  const rootPath = window.Shopify?.routes?.root || "/";
  const normalizedRoot = rootPath.endsWith("/") ? rootPath : `${rootPath}/`;

  const endpoint = (path) => `${normalizedRoot}${String(path).replace(/^\/+/, "")}`;

  const trim = (value, maxLength = 240) => String(value || "").replace(/\s+/g, " ").trim().slice(0, maxLength);

  const htmlToText = (value, maxLength = 320) => {
    const element = document.createElement("div");
    element.innerHTML = String(value || "");
    return trim(element.textContent || element.innerText || "", maxLength);
  };

  const result = (value) => {
    const serialized = JSON.stringify(value);
    if (serialized.length <= MAX_RESULT_LENGTH) {
      return value;
    }

    for (const key of ["products", "variants", "items"]) {
      if (Array.isArray(value?.[key])) {
        return {
          ...value,
          [key]: value[key].slice(0, 5),
          truncated: true,
          message: "The result was shortened. Ask for a narrower query or one product at a time."
        };
      }
    }

    return {
      truncated: true,
      message: "The result was shortened. Ask for a narrower query or one product at a time."
    };
  };

  const readJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
      ...options
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      const message = payload?.description || payload?.message || `Shopify returned HTTP ${response.status}.`;
      throw new Error(trim(message, 240));
    }

    return payload;
  };

  const cleanHandle = (handle) => {
    const value = String(handle || "").trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]*$/.test(value)) {
      throw new Error("Product handle must contain only letters, numbers, and hyphens.");
    }
    return value;
  };

  const cleanQuery = (query) => {
    const value = String(query || "").trim();
    if (value.length < 1 || value.length > 120) {
      throw new Error("Search query must contain between 1 and 120 characters.");
    }
    return value;
  };

  const cleanVariantId = (variantId) => {
    const value = String(variantId || "").trim();
    if (!/^\d{1,20}$/.test(value)) {
      throw new Error("variant_id must be a Shopify variant ID.");
    }
    return value;
  };

  const cleanQuantity = (quantity, { allowZero = false } = {}) => {
    const value = Number(quantity);
    const minimum = allowZero ? 0 : 1;
    if (!Number.isInteger(value) || value < minimum || value > 99) {
      throw new Error(`Quantity must be an integer between ${minimum} and 99.`);
    }
    return value;
  };

  const cleanLineKey = (key) => {
    const value = String(key || "").trim();
    if (value.length < 1 || value.length > 200 || /[\u0000-\u001f\u007f]/.test(value)) {
      throw new Error("line_item_key is invalid.");
    }
    return value;
  };

  const cartSectionId = () => {
    const section = document.querySelector("cart-drawer")?.closest('[id^="shopify-section-"]');
    return section?.id?.replace(/^shopify-section-/, "") || null;
  };

  const cartSections = async (cart, signal) => {
    const sectionId = cartSectionId();
    if (!sectionId) {
      return cart;
    }

    const sectionUrl = new URL(endpoint(""), window.location.origin);
    sectionUrl.searchParams.set("section_id", sectionId);
    const sectionResponse = await fetch(sectionUrl, {
      credentials: "same-origin",
      headers: { Accept: "text/html" },
      signal
    });

    if (sectionResponse.ok) {
      const sections = await sectionResponse.text();
      return { ...cart, sections: { [sectionId]: sections } };
    }

    return cart;
  };

  const emitCartChange = (cart, baseEvent) => {
    const sectionId = cartSectionId();
    const hasDrawerSection = sectionId && cart?.sections?.[sectionId];

    if (hasDrawerSection) {
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent, cart }
      }));
    } else {
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    }
  };

  const getCart = async ({ signal } = {}) => cartSections(await readJson(endpoint("cart.js"), { signal }), signal);

  const cartSummary = (cart) => result({
    currency: cart.currency,
    item_count: cart.item_count,
    total_price: cart.total_price,
    items: (cart.items || []).slice(0, 20).map((item) => ({
      key: item.key,
      product_id: item.product_id,
      variant_id: item.variant_id,
      title: trim(item.product_title || item.title, 120),
      variant_title: trim(item.variant_title, 80),
      quantity: item.quantity,
      price: item.final_line_price,
      url: item.url
    }))
  });

  const annotateForm = (form, name, description) => {
    if (!form || form.hasAttribute("toolname")) {
      return;
    }

    form.setAttribute("toolname", name);
    form.setAttribute("tooldescription", description);
    form.setAttribute("data-webmcp-tool", name);
  };

  const annotateField = (form, selector, description) => {
    form?.querySelector(selector)?.setAttribute("toolparamdescription", description);
  };

  const annotateDeclarativeForms = () => {
    document.querySelectorAll("form").forEach((form) => {
      if (form.querySelector('[name="contact[tags]"][value="newsletter"]')) {
        annotateForm(form, "subscribe_newsletter", "Prepare a newsletter subscription for the visitor. The visitor confirms by submitting the visible form.");
        annotateField(form, '[name="contact[email]"]', "Email address that should receive Peppermint news and offers.");
        return;
      }

      if (form.querySelector('[name="contact[body]"]')) {
        annotateForm(form, "submit_contact_request", "Prepare a customer support message for Peppermint. The visitor confirms by submitting the visible form.");
        annotateField(form, '[name="contact[name]"]', "Visitor's full name.");
        annotateField(form, '[name="contact[email]"]', "Visitor's reply-to email address.");
        annotateField(form, '[name="contact[body]"]', "The visitor's question or support message.");
      }
    });
  };

  const announceToolEvent = (message) => {
    let status = document.querySelector("[data-webmcp-status]");
    if (!status) {
      status = document.createElement("div");
      status.setAttribute("data-webmcp-status", "");
      status.className = "sr-only";
      status.setAttribute("aria-live", "polite");
      document.body.append(status);
    }
    status.textContent = message;
  };

  annotateDeclarativeForms();
  document.addEventListener("shopify:section:load", annotateDeclarativeForms);

  const modelContext = document.modelContext;
  if (!modelContext || typeof modelContext.registerTool !== "function") {
    return;
  }

  if (typeof modelContext.addEventListener === "function") {
    modelContext.addEventListener("toolactivated", ({ toolName }) => {
      announceToolEvent(`Agent activated ${toolName}. Review the visible form before submitting.`);
    });
    modelContext.addEventListener("toolcancel", ({ toolName }) => {
      announceToolEvent(`Agent cancelled ${toolName}.`);
    });
  }

  const register = async (tool) => {
    try {
      await modelContext.registerTool(tool);
    } catch (error) {
      // A single unsupported or already-registered tool must not affect the
      // remaining tools or the normal storefront JavaScript.
      console.warn(`[WebMCP] Could not register ${tool.name}:`, error);
    }
  };

  const registerTools = async () => {
    await register({
      name: "search_products",
      description: "Search Peppermint products by customer language and return matching products with title, handle, URL, price, and availability.",
      inputSchema: {
        type: "object",
        properties: {
          query: { type: "string", description: "Words the customer used to describe the products they want." },
          limit: { type: "number", description: "Maximum number of products to return, from 1 to 10." }
        },
        required: ["query"]
      },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async ({ query, limit = 10 }, { signal } = {}) => {
        const searchQuery = cleanQuery(query);
        const count = Math.min(10, Math.max(1, Number.isInteger(Number(limit)) ? Number(limit) : 10));
        const url = new URL(endpoint("search/suggest.json"), window.location.origin);
        url.searchParams.set("q", searchQuery);
        url.searchParams.set("resources[type]", "product");
        url.searchParams.set("resources[limit]", String(count));
        url.searchParams.set("resources[limit_scope]", "each");
        const payload = await readJson(url, { signal });
        const products = payload?.resources?.results?.products || [];

        return result({
          query: searchQuery,
          products: products.slice(0, count).map((product) => ({
            title: trim(product.title, 120),
            handle: product.handle,
            url: product.url,
            price: product.price,
            available: product.available
          }))
        });
      }
    });

    await register({
      name: "get_product",
      description: "Read one Peppermint product by handle and return its current price, availability, description, and purchasable variants.",
      inputSchema: {
        type: "object",
        properties: {
          handle: { type: "string", description: "The product handle from a Peppermint product URL." }
        },
        required: ["handle"]
      },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async ({ handle }, { signal } = {}) => {
        const productHandle = cleanHandle(handle);
        const product = await readJson(endpoint(`products/${productHandle}.js`), { signal });
        return result({
          title: trim(product.title, 120),
          handle: product.handle,
          url: `${window.location.origin}${endpoint(`products/${product.handle}`)}`,
          description: htmlToText(product.description, 320),
          available: product.available,
          price: product.price,
          currency: window.Shopify?.currency?.active || null,
          variants: (product.variants || []).slice(0, 10).map((variant) => ({
            id: String(variant.id),
            title: trim(variant.title, 80),
            available: variant.available,
            price: variant.price
          }))
        });
      }
    });

    await register({
      name: "get_cart",
      description: "Read the current Peppermint cart and return its items, quantities, prices, currency, and item count.",
      inputSchema: { type: "object", properties: {} },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (_, { signal } = {}) => cartSummary(await getCart({ signal }))
    });

    await register({
      name: "add_to_cart",
      description: "Add an available product variant to the Peppermint cart. The browser or agent should request confirmation before changing the cart.",
      inputSchema: {
        type: "object",
        properties: {
          variant_id: { type: "string", description: "Shopify variant ID for the exact product option the customer chose." },
          quantity: { type: "number", description: "Number of units to add, from 1 to 99." }
        },
        required: ["variant_id"]
      },
      annotations: { readOnlyHint: false, consequentialHint: true, untrustedContentHint: true },
      execute: async ({ variant_id, quantity = 1 }, { signal } = {}) => {
        const variantId = cleanVariantId(variant_id);
        const count = cleanQuantity(quantity);
        const body = new URLSearchParams({ id: variantId, quantity: String(count) });
        const response = await fetch(endpoint("cart/add.js"), {
          method: "POST",
          body,
          credentials: "same-origin",
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          signal
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(trim(payload?.description || payload?.message || "This variant could not be added to the cart.", 240));
        }

        const cart = await getCart();
        emitCartChange(cart, "webmcp:add-to-cart");
        return result({ added_variant_id: variantId, quantity: count, cart: cartSummary(cart) });
      }
    });

    await register({
      name: "update_cart_line",
      description: "Change one Peppermint cart line quantity. Use quantity 0 to remove the line and return the updated cart.",
      inputSchema: {
        type: "object",
        properties: {
          line_item_key: { type: "string", description: "The line item key returned by get_cart." },
          quantity: { type: "number", description: "New quantity, from 0 to 99. Zero removes the line." }
        },
        required: ["line_item_key", "quantity"]
      },
      annotations: { readOnlyHint: false, consequentialHint: true, untrustedContentHint: true },
      execute: async ({ line_item_key, quantity }, { signal } = {}) => {
        const lineKey = cleanLineKey(line_item_key);
        const count = cleanQuantity(quantity, { allowZero: true });
        const body = new URLSearchParams({ id: lineKey, quantity: String(count) });
        const response = await fetch(endpoint("cart/change.js"), {
          method: "POST",
          body,
          credentials: "same-origin",
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          signal
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(trim(payload?.description || payload?.message || "This cart line could not be updated.", 240));
        }

        const cart = await getCart();
        emitCartChange(cart, "webmcp:update-cart-line");
        return result({ line_item_key: lineKey, quantity: count, cart: cartSummary(cart) });
      }
    });

    await register({
      name: "get_page_context",
      description: "Read the current Peppermint storefront context so an agent can choose the next visible action.",
      inputSchema: { type: "object", properties: {} },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async () => result({
        page_type: window.themeVariables?.settings?.pageType || null,
        path: window.location.pathname,
        title: document.title,
        currency: window.Shopify?.currency?.active || null,
        locale: document.documentElement.lang || null,
        cart_item_count: (await getCart()).item_count
      })
    });
  };

  registerTools();
})();
